import React, { useEffect, useState } from 'react';

import '../../Assets/Pages/notification.css';
import { Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { fetchApi } from '../../Network/NetworkService';
import { notif } from '../Ui/Notification';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import ReactEcharts from 'echarts-for-react';
import isoDate from '../../Services/toIsoDate';
import dateFormat from 'dateformat';
import IconTooltip from '../Ui/IconTooltip';
import { rqState } from '../../Constants';

const typeStates = [
    { value: 'acne', label: 'acne' },
    { value: 'asthme', label: 'asthme' },
    { value: 'cardiologie', label: 'cardiologie' },
    { value: 'conjonctivite', label: 'conjonctivite' },
    { value: 'contraception', label: 'contraception' },
    { value: 'covid19', label: 'covid19' },
    { value: 'cystite', label: 'cystite' },
    { value: 'dentiste', label: 'dentiste' },
    { value: 'dermatologie', label: 'dermatologie' },
    { value: 'douleurs-de-dos', label: 'douleurs-de-dos' },
    { value: 'dysfonctionnement-erectile', label: 'dysfonctionnement-erectile' },
    { value: 'generaliste', label: 'generaliste' },
    { value: 'gynecologie', label: 'gynecologie' },
    { value: 'hemorroides', label: 'hemorroides' },
    { value: 'herpes', label: 'herpes' },
    { value: 'infection urinaire', label: 'infection urinaire' },
    { value: 'kit-voyage-et-vaccination', label: 'kit-voyage-et-vaccination' },
    { value: 'maladie-sexuelle', label: 'maladie-sexuelle' },
    { value: 'ophtalmologie', label: 'ophtalmologie' },
    { value: 'pediatrie', label: 'pediatrie' },
    { value: 'psychologie', label: 'psychologie' },
    { value: 'renouvellement-ordonnance', label: 'renouvellement-ordonnance' },
    { value: 'test-de-grossesse', label: 'test-de-grossesse' },
];
const sourceAcquisition = [
    { value: 'google', label: 'Google' },
    { value: 'bing', label: 'Bing' },
    { value: 'skycall', label: 'Skycall (sauf Skycall Voice)' },
    { value: 'skycall-voice', label: 'Skycall Voice' },
    { value: 'gescall', label: 'Gescall' },
    { value: 'medifil', label: 'Medifil' },
    { value: 'infirmiers', label: 'Infirmiers' },
    { value: 'amezian', label: 'Amezian' },
    { value: 'chatgpt', label: 'Chatgpt' },
    { value: 'crechmed', label: 'Crechmed' },
    { value: null, label: 'No source' },
    { value: 'cortel', label: 'Cortel' },
    { value: 'pharmacy', label: 'Pharmacie' },
];

// Friendly labels for utm_source values discovered from the data.
// Any raw value not listed here is displayed verbatim.
const SOURCE_LABELS = {
    google: 'Google',
    bing: 'Bing',
    gescall: 'Gescall',
    medifil: 'Medifil',
    infirmiers: 'Infirmiers',
    amezian: 'Amezian',
    chatgpt: 'Chatgpt',
    crechmed: 'Crechmed',
    cortel: 'Cortel',
};

// Build the source list used by the breakdown table/chart from the actual
// request data, so utm_source values that are not in the curated dropdown
// (e.g. "chatgpt.com", "facebook", ...) still appear instead of being dropped.
const buildDisplaySources = (requests) => {
    // skycall is split into two synthetic buckets by utm_content; pharmacy uses
    // utm_pharmacy. Always include them so the special filter logic kicks in.
    const list = [
        { value: 'skycall', label: 'Skycall (sauf Skycall Voice)' },
        { value: 'skycall-voice', label: 'Skycall Voice' },
        { value: 'pharmacy', label: 'Pharmacie' },
    ];
    const seen = new Set(['skycall', 'skycall-voice', 'pharmacy']);
    let hasNull = false;
    for (const r of requests) {
        const src = r.utm_source;
        if (src == null) { hasNull = true; continue; }
        if (src === 'skycall') continue; // handled by synthetic buckets above
        if (seen.has(src)) continue;
        seen.add(src);
        list.push({ value: src, label: SOURCE_LABELS[src] || src });
    }
    if (hasNull) list.push({ value: null, label: 'No source' });
    return list;
};

const types = [
    { value: 'urgent', label: 'Urgent' },
    { value: 'non', label: 'Normal' },
];

const PIE_COLORS = [
    '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
    '#0ea5e9', '#ef4444', '#14b8a6', '#a855f7', '#f97316',
    '#22c55e', '#06b6d4', '#eab308',
];

const card = {
    background: 'white',
    borderRadius: 16,
    padding: 20,
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
    border: '1px solid #eef2f7',
};

const selectStyles = {
    control: (s) => ({
        ...s,
        borderRadius: 10,
        borderColor: '#e2e8f0',
        minHeight: 38,
        boxShadow: 'none',
        ':hover': { borderColor: '#c7d2fe' },
    }),
    option: (s) => ({ ...s, color: 'black' }),
};

const Acquisition = ({ history }) => {
    const [range, setRange] = useState([new Date(), new Date()]);
    const [state, setState] = useState([]);
    const [typeState, setTypeState] = useState([]);
    const [requests, setRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [filteredSource, setFilteredSource] = useState(null);
    const [loading, setLoading] = useState(false);

    const setOptions = (valueDatas, nameDatas) => {
        let option = {
            tooltip: {
                trigger: 'item',
                formatter: (value) => value.name + ' : ' + (value.value * 100).toFixed(2) + '%',
            },
            color: PIE_COLORS,
            legend: {
                bottom: 0,
                type: 'scroll',
                textStyle: { fontSize: 11, color: '#64748b' },
            },
            series: [
                {
                    name: '',
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: true,
                    itemStyle: {
                        borderRadius: 6,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: { show: false },
                    data: [],
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 12,
                            shadowOffsetX: 0,
                            shadowColor: 'rgba(99, 102, 241, 0.35)',
                        },
                    },
                },
            ],
        };
        if (!valueDatas) return option;
        option.series[0].data = valueDatas.map((el, i) => ({ value: el, name: nameDatas[i] }));
        return option;
    };

    const buildUrl = () =>
        `/requests/analytics?start=${isoDate(range[0], 0, 0)}&end=${isoDate(range[1], 23, 59, 59)}${
            state.length > 0 ? `&state=${state.map((v) => v.value).toString()}` : ''
        }${typeState.length > 0 ? `&typeState=${typeState.map((v) => v.value).toString()}` : ''}${
            filteredSource ? `&utm_source=${filteredSource.value}` : ''
        }`;

    const getRequests = () => {
        setLoading(true);
        fetchApi(buildUrl())
            .then((response) => {
                setRequests(response.requests);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                notif.addNotification('Erreur', 'Erreur de recuperation des donnees: ' + err, 'warning');
            });
    };

    const getFilteredRequests = () => {
        fetchApi(buildUrl())
            .then((response) => setFilteredRequests(response.requests))
            .catch((err) => {
                notif.addNotification('Erreur', 'Erreur de recuperation des donnees: ' + err, 'warning');
            });
    };

    useEffect(() => {
        getRequests();
    }, []);

    const filterSource = (arr, source) => {
        let result;
        if (source === 'pharmacy') {
            result = arr.filter((el) => el.utm_pharmacy && el.utm_pharmacy.includes(source)).length;
        } else if (source === 'skycall-voice') {
            result = arr.filter(
                (el) =>
                    (el.utm_source === 'skycall' && el.utm_content && el.utm_content.includes('voice')) ||
                    el.skycallVoice,
            ).length;
        } else if (source === 'skycall') {
            result = arr.filter(
                (el) => el.utm_source === 'skycall' && el.utm_content && !el.utm_content.includes('voice'),
            ).length;
        } else {
            result = arr.filter((el) => el.utm_source === source).length;
        }
        return result;
    };

    const filterRecurrent = (arr, source) => {
        let startDate = range[0];
        let endDate = range[1];
        let result;
        if (source === 'pharmacy') {
            result = arr
                .filter((el) => el.utm_pharmacy && el.utm_pharmacy.includes(source))
                .filter(
                    (el) =>
                        new Date(el.userId?.createdAt) >= new Date(isoDate(startDate, 0, 0)) &&
                        new Date(el.userId?.createdAt) <= new Date(isoDate(endDate, 23, 59, 59)) &&
                        el.userId.request.length > 1,
                ).length;
        } else if (source === 'skycall-voice') {
            result = arr
                .filter(
                    (el) =>
                        (el.utm_source === 'skycall' && el.utm_content && el.utm_content.includes('voice')) ||
                        el.skycallVoice,
                )
                .filter(
                    (el) =>
                        new Date(el.userId?.createdAt) >= new Date(isoDate(startDate, 0, 0)) &&
                        new Date(el.userId?.createdAt) <= new Date(isoDate(endDate, 23, 59, 59)) &&
                        el.userId.request.length > 1,
                ).length;
        } else if (source === 'skycall') {
            result = arr
                .filter(
                    (el) =>
                        el.utm_source === 'skycall' && el.utm_content && !el.utm_content.includes('voice'),
                )
                .filter(
                    (el) =>
                        new Date(el.userId?.createdAt) >= new Date(isoDate(startDate, 0, 0)) &&
                        new Date(el.userId?.createdAt) <= new Date(isoDate(endDate, 23, 59, 59)) &&
                        el.userId.request.length > 1,
                ).length;
        } else {
            result = arr
                .filter((el) => el.utm_source === source)
                .filter(
                    (el) =>
                        new Date(el.userId?.createdAt) >= new Date(isoDate(startDate, 0, 0)) &&
                        new Date(el.userId?.createdAt) <= new Date(isoDate(endDate, 23, 59, 59)) &&
                        el.userId.request.length > 1,
                ).length;
        }
        if (source === 'medifil') {
            result = arr
                .filter((el) => el.utm_source === source)
                .filter(
                    (el) =>
                        new Date(el.userId?.createdAt) >= new Date(isoDate(startDate, 0, 0)) &&
                        new Date(el.userId?.createdAt) <= new Date(isoDate(endDate, 23, 59, 59)) &&
                        el.userId.request.length > 1,
                ).length;
        }
        return result;
    };

    const filterType = (arr, type) => {
        if (type === 'urgent') return arr.filter((el) => el.priority).length;
        return arr.filter((el) => !el.priority).length;
    };

    const total = requests.length;
    const urgentCount = filterType(requests, 'urgent');
    const normalCount = filterType(requests, 'non');
    const urgentPct = total ? ((urgentCount / total) * 100).toFixed(1) : '0.0';

    const displaySources = buildDisplaySources(requests);
    const sourceCounts = displaySources.reduce((acc, s) => {
        acc.set(s.value, filterSource(requests, s.value));
        acc.recurrent.set(s.value, filterRecurrent(requests, s.value));
        return acc;
    }, Object.assign(new Map(), { recurrent: new Map() }));
    const countFor = (v) => sourceCounts.get(v) || 0;
    const recurrentFor = (v) => sourceCounts.recurrent.get(v) || 0;

    const sortedSources = [...displaySources].sort(
        (a, b) => countFor(b.value) - countFor(a.value),
    );
    const topSource = total > 0 && countFor(sortedSources[0]?.value) > 0 ? sortedSources[0] : null;
    const topSourceCount = topSource ? countFor(topSource.value) : 0;

    const kpis = [
        {
            label: 'Total requêtes',
            value: total.toLocaleString('fr-FR'),
            icon: '📊',
            accent: '#6366f1',
            sub: `${dateFormat(range[0], 'dd/mm/yy')} → ${dateFormat(range[1], 'dd/mm/yy')}`,
        },
        {
            label: 'Urgent',
            value: urgentCount.toLocaleString('fr-FR'),
            icon: '⚡',
            accent: '#ef4444',
            sub: `${urgentPct}% du total`,
        },
        {
            label: 'Normal',
            value: normalCount.toLocaleString('fr-FR'),
            icon: '🟢',
            accent: '#10b981',
            sub: `${total ? (100 - parseFloat(urgentPct)).toFixed(1) : '0.0'}% du total`,
        },
        {
            label: 'Top source',
            value: topSource ? topSource.label : '—',
            icon: '🏆',
            accent: '#f59e0b',
            sub: topSource ? `${topSourceCount.toLocaleString('fr-FR')} requêtes` : 'Aucune donnée',
        },
    ];

    return (
        <div className="r-analytics" style={{ padding: '8px 4px 40px' }}>
            {/* Hero */}
            <div
                style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                    borderRadius: 16,
                    padding: '28px 32px',
                    color: 'white',
                    boxShadow: '0 10px 30px -10px rgba(99, 102, 241, 0.5)',
                    marginBottom: 20,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <div style={{ position: 'absolute', right: -40, top: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ position: 'absolute', right: 80, bottom: -60, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative' }}>
                    <div
                        style={{
                            width: 56,
                            height: 56,
                            borderRadius: 14,
                            background: 'rgba(255,255,255,0.18)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 28,
                            backdropFilter: 'blur(8px)',
                        }}
                    >
                        🎯
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontWeight: 700, fontSize: 24, color: '#fff' }}>Tracking d'acquisitions</h2>
                        <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 14, color: '#fff' }}>
                            Analysez la provenance des requêtes et leur récurrence par source.
                        </p>
                    </div>
                </div>
            </div>

            {/* Filters card */}
            <div style={{ ...card, marginBottom: 20 }}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: 12,
                        alignItems: 'end',
                    }}
                >
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 6 }}>
                            📅 Période
                        </label>
                        <DateRangePicker onChange={(value) => setRange(value)} value={range} maxDate={new Date()} />
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 6 }}>
                            🌐 Source d'acquisition
                        </label>
                        <Select
                            value={sourceAcquisition.filter((option) => option === filteredSource)}
                            defaultValue={filteredSource?.value || ''}
                            onChange={(v) => setFilteredSource(v)}
                            options={sourceAcquisition}
                            styles={selectStyles}
                            placeholder="Toutes les sources"
                            isClearable
                            className="filter-status"
                        />
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 6 }}>
                            🏷️ Statut
                        </label>
                        <Select
                            value={state}
                            onChange={(v) => setState(v)}
                            options={rqState}
                            isMulti
                            styles={selectStyles}
                            placeholder="Tous les statuts"
                            className="filter-status"
                        />
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 6 }}>
                            🩺 Pathologie
                        </label>
                        <Select
                            value={typeState}
                            onChange={(v) => setTypeState(v)}
                            options={typeStates}
                            isMulti
                            styles={selectStyles}
                            placeholder="Toutes les pathologies"
                            className="filter-status"
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                    <button
                        onClick={() => {
                            getRequests();
                            getFilteredRequests();
                        }}
                        disabled={loading}
                        style={{
                            background: loading
                                ? '#94a3b8'
                                : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '10px 26px',
                            borderRadius: 10,
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            boxShadow: loading ? 'none' : '0 4px 12px rgba(99, 102, 241, 0.35)',
                            transition: 'transform 0.15s, box-shadow 0.15s',
                        }}
                        onMouseEnter={(e) => {
                            if (loading) return;
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(99, 102, 241, 0.45)';
                        }}
                        onMouseLeave={(e) => {
                            if (loading) return;
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.35)';
                        }}
                    >
                        {loading ? 'Chargement...' : '🔍  Chercher'}
                    </button>
                </div>
            </div>

            {/* KPI cards */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 14,
                    marginBottom: 20,
                }}
            >
                {kpis.map((k) => (
                    <div
                        key={k.label}
                        style={{
                            ...card,
                            position: 'relative',
                            overflow: 'hidden',
                            padding: 18,
                            transition: 'transform 0.15s, box-shadow 0.15s',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 12px 24px -12px rgba(15, 23, 42, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = card.boxShadow;
                        }}
                    >
                        <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: k.accent }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                            <div
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 10,
                                    background: `${k.accent}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 18,
                                }}
                            >
                                {k.icon}
                            </div>
                            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                {k.label}
                            </div>
                        </div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{k.value}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{k.sub}</div>
                    </div>
                ))}
            </div>

            {/* Urgent panel */}
            <div style={{ ...card, marginBottom: 20 }}>
                <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>⚡</span> Consultations urgentes vs. normales
                </h3>
                <Row>
                    <Col lg={7} md={6} sm={12} xs={12}>
                        <div style={{ overflow: 'hidden', borderRadius: 12, border: '1px solid #eef2f7' }}>
                            <table className="table table-hover mb-0 analytic-table" style={{ margin: 0 }}>
                                <thead style={{ background: '#f8fafc' }}>
                                    <tr>
                                        <th>Type</th>
                                        <th>Nombre de requêtes</th>
                                        <th>Pourcentage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {types.map((type) => {
                                        const count = filterType(requests, type.value);
                                        const pct = total ? ((count / total) * 100).toFixed(2) : '0.00';
                                        return (
                                            <tr key={type.value}>
                                                <td>
                                                    <span
                                                        style={{
                                                            display: 'inline-block',
                                                            padding: '3px 10px',
                                                            borderRadius: 999,
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            background: type.value === 'urgent' ? '#fef2f2' : '#ecfdf5',
                                                            color: type.value === 'urgent' ? '#dc2626' : '#059669',
                                                        }}
                                                    >
                                                        {type.label}
                                                    </span>
                                                </td>
                                                <td><strong>{count}</strong></td>
                                                <td>{pct} %</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Col>
                    <Col lg={5} md={6} sm={12} xs={12}>
                        <ReactEcharts
                            option={setOptions(
                                types.map((type) => filterType(requests, type.value) / (total || 1)),
                                types.map((type) => type.label),
                            )}
                            style={{ height: '280px', width: '100%' }}
                        />
                    </Col>
                </Row>
            </div>

            {/* Source breakdown panel */}
            <div style={{ ...card, marginBottom: 20 }}>
                <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>🌐</span> Par source d'acquisition
                </h3>
                <Row>
                    <Col lg={7} md={6} sm={12} xs={12}>
                        <div style={{ overflow: 'hidden', borderRadius: 12, border: '1px solid #eef2f7' }}>
                            <table className="table table-hover mb-0 analytic-table" style={{ margin: 0 }}>
                                <thead style={{ background: '#f8fafc' }}>
                                    <tr>
                                        <th>Source d'acquisition</th>
                                        <th>Requêtes</th>
                                        <th>%</th>
                                        <th>Récurrence</th>
                                        <th>% Récurrence</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedSources
                                        .map((source) => ({
                                            source,
                                            cnt: countFor(source.value),
                                            rec: recurrentFor(source.value),
                                        }))
                                        .filter((row) => row.cnt > 0)
                                        .map(({ source, cnt, rec }) => {
                                            const pct = total ? ((cnt / total) * 100).toFixed(2) : '0.00';
                                            const recPct = ((rec / cnt) * 100).toFixed(2);
                                            const recPctNum = parseFloat(recPct);
                                            let recColor = '#ef4444';
                                            if (recPctNum >= 30) recColor = '#10b981';
                                            else if (recPctNum >= 15) recColor = '#f59e0b';
                                            return (
                                                <tr key={source.value || 'no-source'}>
                                                    <td><strong style={{ color: '#334155' }}>{source.label}</strong></td>
                                                    <td>{cnt}</td>
                                                    <td><span style={{ color: '#64748b' }}>{pct} %</span></td>
                                                    <td>{rec}</td>
                                                    <td>
                                                        <span style={{ color: recColor, fontWeight: 700 }}>
                                                            {recPct} %
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    {sortedSources.every((s) => countFor(s.value) === 0) && (
                                        <tr>
                                            <td colSpan="5" style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                                                Aucune source d'acquisition pour cette période.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Col>
                    <Col lg={5} md={6} sm={12} xs={12}>
                        <ReactEcharts
                            option={(() => {
                                const nonZero = displaySources.filter((s) => countFor(s.value) > 0);
                                return setOptions(
                                    nonZero.map((s) => countFor(s.value) / (total || 1)),
                                    nonZero.map((s) => s.label),
                                );
                            })()}
                            style={{ height: '380px', width: '100%' }}
                        />
                    </Col>
                </Row>
            </div>

            {/* Filtered requests detail */}
            {filteredSource && (
                <div style={card}>
                    <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>📋</span> Détail —{' '}
                        <span
                            style={{
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {filteredSource.label}
                        </span>
                        {filteredRequests.length > 0 && (
                            <span
                                style={{
                                    background: '#eef2ff',
                                    color: '#6366f1',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    padding: '2px 10px',
                                    borderRadius: 999,
                                }}
                            >
                                {filteredRequests.length}
                            </span>
                        )}
                    </h3>
                    {filteredRequests.length > 0 ? (
                        <div className="table-responsive" style={{ overflow: 'hidden', borderRadius: 12, border: '1px solid #eef2f7' }}>
                            <table className="table table-hover mb-0" style={{ margin: 0 }}>
                                <thead style={{ background: '#f8fafc' }}>
                                    <tr>
                                        <th>Utilisateur</th>
                                        <th>Email</th>
                                        <th>Date de création</th>
                                        <th>Type</th>
                                        <th>État</th>
                                        <th>Docteur</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRequests.map((r) => (
                                        <tr key={r._id}>
                                            <td>{r.userId?.FName} {r.userId?.LName}</td>
                                            <td><span style={{ color: '#64748b' }}>{r.userId?.email}</span></td>
                                            <td>{dateFormat(r.createdAt, 'dd/mm/yy HH:MM')}</td>
                                            <td><span style={{ fontSize: 12, padding: '2px 8px', background: '#eef2ff', color: '#6366f1', borderRadius: 6, fontWeight: 600 }}>{r.type}</span></td>
                                            <td>{r.state === 'waiting' && r.notOnlineReason ? 'Indisponible à distance' : rqState.find((el) => el.value === r.state)?.label}</td>
                                            <td>{r.doctorId ? r.doctorId.FName + ' ' + r.doctorId.LName : '—'}</td>
                                            <td>
                                                <IconTooltip
                                                    icon="fas fa-file-medical"
                                                    text="Details"
                                                    onClick={() => history && history.push('/requests/detail/' + r._id)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                            Cliquez sur "Chercher" pour charger le détail.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Acquisition;
